<template>
  <div v-if="show" class="modal-overlay" @click="handleOverlayClick" @touchmove.prevent>
    <div class="modal-content error-modal" @click.stop @touchmove.stop>
      <div class="modal-header">
        <div class="header-icon">
          <i class="fa-solid fa-exclamation-triangle"></i>
        </div>
        <div class="header-content">
          <h3 class="card-title">Request Failed</h3>
          <p class="card-subtitle">{{ errorMessage }}</p>
        </div>
        <button type="button" class="btn-close" @click="closeModal">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body" v-if="errorDetails && errorDetails.length > 0">
        <div class="error-details">
          <label class="form-label fw-semibold">
            <i class="fa-solid fa-list me-1"></i>Details
          </label>
          <div class="details-container">
            <ul class="error-list">
              <li v-for="(detail, index) in errorDetails" :key="index" class="error-item">
                {{ detail }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button type="button" class="btn btn-primary btn-lg" @click="closeModal">
          <i class="fa-solid fa-check me-1"></i>
          Understood
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  error: {
    type: [Object, String],
    default: null
  },
  allowOverlayClose: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['close'])

// iOS Safari viewport fix
let originalViewportHeight = null

const fixIOSViewport = () => {
  if (typeof window !== 'undefined') {
    // Store original viewport height
    originalViewportHeight = window.innerHeight
    
    // Set fixed height to prevent iOS Safari viewport changes
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`)
    
    // Prevent body scroll on iOS
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'
    document.body.style.height = '100%'
  }
}

const restoreIOSViewport = () => {
  if (typeof window !== 'undefined') {
    // Restore body scroll
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.width = ''
    document.body.style.height = ''
    
    // Remove custom viewport height
    document.documentElement.style.removeProperty('--vh')
  }
}

// Watch for show prop changes to handle iOS fixes
const handleShowChange = (newShow) => {
  if (newShow) {
    fixIOSViewport()
  } else {
    restoreIOSViewport()
  }
}

// Apply iOS fixes when modal shows/hides
onMounted(() => {
  if (props.show) {
    fixIOSViewport()
  }
})

onUnmounted(() => {
  restoreIOSViewport()
})

// Watch show prop
import { watch } from 'vue'
watch(() => props.show, handleShowChange)

const errorMessage = computed(() => {
  if (!props.error) return 'An unexpected error occurred'
  
  if (typeof props.error === 'string') {
    return props.error
  }
  
  if (props.error.error) {
    return props.error.error
  }
  
  if (props.error.message) {
    return props.error.message
  }
  
  return 'An unexpected error occurred'
})

const errorDetails = computed(() => {
  if (!props.error || typeof props.error === 'string') {
    return []
  }
  
  if (props.error.details && Array.isArray(props.error.details)) {
    return props.error.details
  }
  
  return []
})

const closeModal = () => {
  emit('close')
}

const handleOverlayClick = () => {
  if (props.allowOverlayClose) {
    closeModal()
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-content {
  background: var(--light-card);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.error-modal {
  border-top: 7px solid var(--light-border);
}

.modal-header {
  display: flex;
  align-items: flex-start;
  padding: 1.5rem;
  border-bottom: 1px solid var(--light-border);
}

.header-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--light-text);
  font-size: 1.25rem;
  flex-shrink: 0;
  border: 1px solid var(--light-text);
  margin-right: 1rem;
}

.header-content {
  flex: 1;
}

.card-title {
  margin: 0 0 0.25rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--light-text);
}

.card-subtitle {
  margin: 0;
  font-size: 0.95rem;
  color: var(--light-text-muted);
  line-height: 1.4;
}

.btn-close {
  background: none;
  border: none;
  color: var(--light-text);
  font-size: 1.25rem;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  margin-left: 1rem;
}

.btn-close:hover {
  background: var(--nested-container-bg);
}

.modal-body {
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
}

.error-details {
  margin-bottom: 0;
}

.form-label {
  color: var(--light-text);
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.details-container {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1rem;
}

.error-list {
  margin: 0;
  padding-left: 1.25rem;
  list-style-type: disc;
}

.error-item {
  color: var(--light-text);
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 0.5rem;
}

.error-item:last-child {
  margin-bottom: 0;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid var(--light-border);
  display: flex;
  justify-content: flex-end;
  background: var(--nested-container-bg);
}

.btn-primary {
  background-color: #36A2EB;
  border-color: #36A2EB;
  min-height: 50px;
  color: white;
}

.btn-primary:hover {
  background-color: #2b8bd6;
  border-color: #2b8bd6;
}

/* Dark mode support */
:global([data-theme]) .header-icon {
  border-color: var(--light-text);
  color: var(--light-text);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
  }
  
  .header-icon {
    width: 40px;
    height: 40px;
    margin-right: 0.75rem;
  }
  
  .card-title {
    font-size: 1.1rem;
  }
  
  .card-subtitle {
    font-size: 0.9rem;
  }
}
</style>